import { organizationQueryKey } from '@cast/constants';
import { makeSearchQuery } from '@cast/shared-search-ui';
import { WorkloadRecommendationStatusTypeEnum } from '@cast/types';
import {
  ERROR_STATUS_TYPE,
  LOW_CONFIDENCE_STATUS_TYPE,
} from '@cast/split-me/features/cluster/workload-optimization';

import { WORKLOAD_CATEGORIES, WORKLOAD_VIEWS } from '../../../_constants';
import { INVENTORY_WORKLOADS_QUERY_PARAM_KEYS } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { inventoryWorkloadsTableKeys } from '../../../consts';
import { buildInventoryWorkloadsViewPath } from '../../../navigation';

/**
 * Criterion keys/values mirror WOOP workloads-list searchCriteria
 * (verticalOptimizationCriterion, horizontalOptimizationCriterion, hpaCriterion, statusCriterion).
 */
const VERTICAL_OPTIMIZATION_KEY = 'verticalOptimization';
const HORIZONTAL_OPTIMIZATION_KEY = 'horizontalOptimization';
const HPA_KEY = 'hpa';
const STATUS_KEY = 'status';

const ON = 'on';
const OFF = 'off';

export type AutoscalingListFilterValues = Record<
  string,
  string[] | boolean | string | number | undefined
>;

const PERSISTENT_QUERY_PARAMS = [
  organizationQueryKey,
  ...Object.values(INVENTORY_WORKLOADS_QUERY_PARAM_KEYS),
];

const appendPersistentQueryParams = (
  path: string,
  queryParameters: URLSearchParams
) => {
  const mergedParams = new URLSearchParams();
  queryParameters.forEach((value, key) => {
    mergedParams.append(key, value);
  });

  const searchParams = new URLSearchParams(window.location.search);
  searchParams.forEach((value, key) => {
    if (PERSISTENT_QUERY_PARAMS.includes(key)) {
      mergedParams.append(key, value);
    }
  });

  const query = mergedParams.toString();
  return query ? `${path}?${query}` : path;
};

type InventoryWorkloadsListLinkArgs = {
  clusterId: string;
};

export const makeInventoryWorkloadsAutoscalingListLink = ({
  clusterId,
  filters,
}: InventoryWorkloadsListLinkArgs & {
  filters: AutoscalingListFilterValues;
}) => {
  const pathname = buildInventoryWorkloadsViewPath({
    clusterId,
    view: WORKLOAD_VIEWS.LIST,
    category: WORKLOAD_CATEGORIES.AUTOSCALING,
  });

  const searchParams = makeSearchQuery({
    id: inventoryWorkloadsTableKeys.AUTOSCALING,
    values: filters,
  });

  return appendPersistentQueryParams(pathname, searchParams);
};

export const makeInventoryWorkloadsComputeListLink = ({
  clusterId,
  sortBy,
}: InventoryWorkloadsListLinkArgs & { sortBy: string }) => {
  const pathname = buildInventoryWorkloadsViewPath({
    clusterId,
    view: WORKLOAD_VIEWS.LIST,
    category: WORKLOAD_CATEGORIES.COMPUTE,
  });

  const searchParams = new URLSearchParams();
  searchParams.set(`${inventoryWorkloadsTableKeys.COMPUTE}_sortBy`, sortBy);

  return appendPersistentQueryParams(pathname, searchParams);
};

export const makeInventoryWorkloadsNetworkListLink = ({
  clusterId,
  sortBy,
}: InventoryWorkloadsListLinkArgs & { sortBy: string }) => {
  const pathname = buildInventoryWorkloadsViewPath({
    clusterId,
    view: WORKLOAD_VIEWS.LIST,
    category: WORKLOAD_CATEGORIES.NETWORK,
  });

  const searchParams = new URLSearchParams();
  searchParams.set(`${inventoryWorkloadsTableKeys.NETWORK}_sortBy`, sortBy);

  return appendPersistentQueryParams(pathname, searchParams);
};

export const COMPUTE_COST_SORT = 'totalCost:desc';
export const BIGGEST_COST_MOVERS_SORT = 'totalCostPercentChange:desc';
export const NETWORK_COST_SORT = 'totalCost:desc';
export const NETWORK_TRAFFIC_SORT = 'totalBytes:desc';

export const COVERAGE_DIMENSION_FILTERS: Record<
  string,
  AutoscalingListFilterValues
> = {
  vertical: {
    [VERTICAL_OPTIMIZATION_KEY]: [ON],
    [HPA_KEY]: false,
  },
  horizontal: {
    [VERTICAL_OPTIMIZATION_KEY]: [OFF],
    [HORIZONTAL_OPTIMIZATION_KEY]: [ON],
  },
  both: {
    [VERTICAL_OPTIMIZATION_KEY]: [ON],
    [HORIZONTAL_OPTIMIZATION_KEY]: [ON],
  },
  disabled: {
    [VERTICAL_OPTIMIZATION_KEY]: [OFF],
    [HORIZONTAL_OPTIMIZATION_KEY]: [OFF],
  },
};

export const RECOMMENDATION_STATUS_FILTERS: Record<
  string,
  AutoscalingListFilterValues | undefined
> = {
  applied: {
    [STATUS_KEY]: [WorkloadRecommendationStatusTypeEnum.APPLIED],
  },
  lowConfidence: {
    [STATUS_KEY]: [LOW_CONFIDENCE_STATUS_TYPE],
  },
  failed: {
    [STATUS_KEY]: [ERROR_STATUS_TYPE],
  },
  notAvailable: undefined,
};
